import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useForm } from "react-hook-form";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import type { Order } from "~/v2/api/rest/account";
import {
  type ReplaceAtdOrderParams,
  type ReplaceAtdOrderPayload,
  replaceAtdOrder,
} from "./api";
import type { OrderReplacementSubmittedResult } from "./OrderReplacementDialog.connector";
import {
  buildOrderReplacementChanges,
  buildOrderReplacementPayload,
  createOrderReplacementInitialValues,
  createOrderReplacementSchema,
  estimateReplaceCost,
  getApplicableReplacementPriceFields,
  getOrderReplacementEligibility,
  getTimeInForceOptions,
  type OrderReplacementChange,
  type OrderReplacementFormValues,
} from "./OrderReplacementDialog.utils";
import { useAtdOrderDetail } from "./useAtdOrderDetail";
import { useInvalidateOrderRelatedQueries } from "./useInvalidateOrderRelatedQueries";

export type OrderReplacementStep = "edit" | "review";

export interface UseOrderReplacementControllerParams {
  order: Order;
  onConfirm: (payload: ReplaceAtdOrderPayload) => void;
}

export interface OrderReplacementController {
  step: OrderReplacementStep;
  values: OrderReplacementFormValues;
  errors: Partial<Record<keyof OrderReplacementFormValues | "form", string>>;
  applicablePriceFields: ReturnType<typeof getApplicableReplacementPriceFields>;
  timeInForceOptions: ReturnType<typeof getTimeInForceOptions>;
  changes: OrderReplacementChange[];
  onFieldChange: (
    field: keyof OrderReplacementFormValues,
    value: string,
  ) => void;
  onReview: () => void;
  onBack: () => void;
  onConfirm: () => void;
  onSubmit: () => void;
  estimatedCost: string;
}

export const useOrderReplacementController = ({
  order,
  onConfirm,
}: UseOrderReplacementControllerParams): OrderReplacementController => {
  const schema = useMemo(() => createOrderReplacementSchema(order), [order]);
  const initialValues = createOrderReplacementInitialValues(order);
  const initialQuantity = initialValues.qty;
  const initialLimitPrice = initialValues.limitPrice;
  const initialStopPrice = initialValues.stopPrice;
  const initialTimeInForce = initialValues.timeInForce;
  const form = useForm<OrderReplacementFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
  });
  const resetForm = form.reset;
  const [step, setStep] = useState<OrderReplacementStep>("edit");
  const [payload, setPayload] = useState<ReplaceAtdOrderPayload>({});
  const values = form.watch();
  const resetKey = [
    order.id,
    order.updated_at,
    order.qty,
    order.filled_qty,
    order.limit_price,
    order.stop_price,
    order.time_in_force,
  ].join(":");

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the identity/value trigger; the body writes reset state instead of reading the key
  useEffect(() => {
    resetForm({
      qty: initialQuantity,
      limitPrice: initialLimitPrice,
      stopPrice: initialStopPrice,
      timeInForce: initialTimeInForce,
    });
    setPayload({});
    setStep("edit");
  }, [
    initialLimitPrice,
    initialQuantity,
    initialStopPrice,
    initialTimeInForce,
    resetForm,
    resetKey,
  ]);

  const handleReview = form.handleSubmit((validValues) => {
    const nextPayload = buildOrderReplacementPayload(order, validValues);
    if (Object.keys(nextPayload).length === 0) {
      form.setError("root", {
        type: "validate",
        message: "Change at least one editable value before reviewing.",
      });
      return;
    }
    form.clearErrors("root");
    setPayload(nextPayload);
    setStep("review");
  });

  const handleSubmit = form.handleSubmit((validValues) => {
    const nextPayload = buildOrderReplacementPayload(order, validValues);
    if (Object.keys(nextPayload).length === 0) {
      form.setError("root", {
        type: "validate",
        message: "Change at least one editable value before replacing.",
      });
      return;
    }
    form.clearErrors("root");
    onConfirm(nextPayload);
  });

  const fieldErrors = form.formState.errors;

  return {
    step,
    values,
    errors: {
      qty: fieldErrors.qty?.message,
      limitPrice: fieldErrors.limitPrice?.message,
      stopPrice: fieldErrors.stopPrice?.message,
      timeInForce: fieldErrors.timeInForce?.message,
      form: fieldErrors.root?.message,
    },
    applicablePriceFields: getApplicableReplacementPriceFields(order),
    timeInForceOptions: getTimeInForceOptions(order.asset_class),
    changes: buildOrderReplacementChanges(order, payload),
    onFieldChange: (field, value) => {
      form.clearErrors("root");
      form.setValue(field, value, {
        shouldDirty: true,
        shouldValidate: Boolean(form.formState.errors[field]),
      });
    },
    onReview: () => {
      void handleReview();
    },
    onBack: () => {
      setStep("edit");
    },
    onConfirm: () => {
      if (Object.keys(payload).length > 0) {
        onConfirm(payload);
      }
    },
    onSubmit: () => {
      void handleSubmit();
    },
    estimatedCost: estimateReplaceCost(values.qty, values.limitPrice),
  };
};

const suppressGlobalReplacementToast = () => {
  // Local, identity-guarded modal feedback owns mutation errors.
};

export interface UseOrderReplacementMutationControllerParams {
  open: boolean;
  accountID?: string;
  product?: "live" | "paper";
  orderID?: string;
  onOpenChange: (open: boolean) => void;
  onSubmitted: (result: OrderReplacementSubmittedResult) => void;
  onMutationPendingChange?: (orderID: string, pending: boolean) => void;
}

export interface OrderReplacementMutationController {
  order?: Order;
  canEdit: boolean;
  eligibilityDisabledReason?: string;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isNotFound: boolean;
  isPending: boolean;
  errorMessage?: string;
  submissionError?: string;
  onConfirm: (payload: ReplaceAtdOrderPayload) => void;
  onOpenChange: (open: boolean) => void;
  onRetry: () => void;
}

export const useOrderReplacementMutationController = ({
  open,
  accountID,
  product,
  orderID,
  onOpenChange,
  onSubmitted,
  onMutationPendingChange,
}: UseOrderReplacementMutationControllerParams): OrderReplacementMutationController => {
  const [submissionError, setSubmissionError] = useState<string>();
  const attemptSequenceRef = useRef(0);
  const activeAttemptRef = useRef<{
    contextKey: string;
    token: number;
  }>();
  const contextKey =
    accountID && product && orderID ? `${product}:${accountID}:${orderID}` : "";
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  const detailQuery = useAtdOrderDetail({
    accountID,
    product,
    orderID,
    enabled: open,
    requireFresh: true,
  });
  const replacementMutation = useMutationRequest(
    (variables: ReplaceAtdOrderParams) => replaceAtdOrder(variables),
    { onError: suppressGlobalReplacementToast },
  );
  const invalidateOrderRelatedQueries = useInvalidateOrderRelatedQueries();

  // biome-ignore lint/correctness/useExhaustiveDependencies: context/open are reset triggers; the body invalidates callbacks and clears local feedback
  useEffect(() => {
    activeAttemptRef.current = undefined;
    setSubmissionError(undefined);
  }, [contextKey, open]);

  const handleConfirm = useCallback(
    (orderDetails: ReplaceAtdOrderPayload) => {
      if (
        !open ||
        !accountID ||
        !product ||
        !orderID ||
        replacementMutation.isPending ||
        activeAttemptRef.current !== undefined ||
        Object.keys(orderDetails).length === 0
      ) {
        return;
      }

      const original = { accountID, product, orderID };
      const attemptContextKey = contextKey;
      const attemptToken = ++attemptSequenceRef.current;
      activeAttemptRef.current = {
        contextKey: attemptContextKey,
        token: attemptToken,
      };
      const isCurrentAttempt = () => {
        const activeAttempt = activeAttemptRef.current;
        return (
          contextKeyRef.current === attemptContextKey &&
          activeAttempt?.contextKey === attemptContextKey &&
          activeAttempt.token === attemptToken
        );
      };

      setSubmissionError(undefined);
      onMutationPendingChange?.(orderID, true);
      replacementMutation.mutate(
        { ...original, orderDetails },
        {
          onSuccess: (replacementOrder) => {
            invalidateOrderRelatedQueries();
            if (!isCurrentAttempt()) {
              return;
            }
            const hasUsableReplacementOrder =
              typeof replacementOrder === "object" &&
              replacementOrder !== null &&
              !Array.isArray(replacementOrder) &&
              typeof replacementOrder.id === "string" &&
              Boolean(replacementOrder.id.trim());
            onSubmitted({
              original,
              replacementOrder: hasUsableReplacementOrder
                ? replacementOrder
                : null,
            });
          },
          onError: (error: Error) => {
            if (isCurrentAttempt()) {
              setSubmissionError(
                `Replacement could not be submitted. ${getErrorMessage(error)}`,
              );
            }
          },
          onSettled: () => {
            if (!isCurrentAttempt()) {
              return;
            }
            activeAttemptRef.current = undefined;
            onMutationPendingChange?.(orderID, false);
          },
        },
      );
    },
    [
      accountID,
      contextKey,
      invalidateOrderRelatedQueries,
      onMutationPendingChange,
      onSubmitted,
      open,
      orderID,
      product,
      replacementMutation,
    ],
  );

  const isPending = replacementMutation.isPending;
  const isWaitingForFreshDetail =
    open && (detailQuery.isLoading || detailQuery.isFetching);
  const isNotFound =
    axios.isAxiosError(detailQuery.error) &&
    detailQuery.error.response?.status === 404;
  const eligibility = detailQuery.data
    ? getOrderReplacementEligibility(detailQuery.data)
    : undefined;
  const eligibilityDisabledReason =
    eligibility && !eligibility.eligible ? eligibility.reason : undefined;
  const canEdit = Boolean(detailQuery.data && eligibility?.eligible);

  return {
    order: detailQuery.data ?? undefined,
    canEdit,
    eligibilityDisabledReason,
    isLoading: isWaitingForFreshDetail,
    isFetching: open && detailQuery.isFetching,
    isError: open && detailQuery.isError,
    isNotFound: open && isNotFound,
    isPending,
    errorMessage: detailQuery.error?.message,
    submissionError,
    onConfirm: handleConfirm,
    onOpenChange: (nextOpen: boolean) => {
      if (!nextOpen && isPending) {
        return;
      }
      onOpenChange(nextOpen);
    },
    onRetry: () => {
      void detailQuery.refetch();
    },
  };
};
